import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowDown } from "lucide-react";
import PageHero from "../components/common/PageHero";
import { Reveal, SectionHeading } from "../components/common/Section";
import { Button } from "../components/ui/button";
import { PLATFORM_FAQS } from "../data/content";

const norm = (items) => items.map((it) => Array.isArray(it) ? { q: it[0], a: it[1] } : it);

const GROUPS = [
  { title: "Platform & accountability", items: norm(PLATFORM_FAQS || []) },
  { title: "Vör — governed chat", items: [
    { q: "Does Vör replace our AI model?", a: "No. Vör is an independent control layer in front of the model. The model stays separate — Vör controls what it can see, say and do." },
    { q: "Can it stop the AI giving advice it shouldn't?", a: "Yes, where configured. Responses can be held, redacted or escalated to a person before they reach the customer." },
    { q: "What happens with a vulnerable customer?", a: "Vör can detect indicators and escalate the conversation to a named reviewer under your policy." },
    { q: "Do you store conversation content?", a: "Retention is configurable; Vör can operate on a minimal-evidence basis, keeping a reference rather than raw content." },
  ] },
  { title: "Lögberg — regulated call review", items: [
    { q: "Does Lögberg approve or reject outcomes?", a: "No. Lögberg screens, prioritises and records — the decision stays with your reviewers. It supports oversight, it does not replace it." },
    { q: "Is it FCA-approved?", a: "No product is FCA-approved. Lögberg is designed to support alignment with your obligations; your firm remains accountable." },
    { q: "Does it review every call?", a: "It can screen all captured conversations and surface the material ones for human review, rather than sampling a small percentage." },
  ] },
  { title: "Skjold & Defence — enterprise AI control", items: [
    { q: "How is Skjold different from Defence?", a: "Skjold focuses on blocking sensitive documents in web AI tools. Defence is the broader runtime-control product for employee AI, copilots and agents — Skjold-style document control is one part of it." },
    { q: "Can Defence stop an agent taking an action?", a: "Where configured, Defence can inspect and block an out-of-policy action before it executes, and escalate to an owner." },
    { q: "Does Skjold retain document content?", a: "No. Skjold scans locally before anything is shared and retains no document content." },
  ] },
  { title: "Trust & assurance", items: [
    { q: "Which certifications do you hold?", a: "ISO 9001 (quality) and ISO 42001 (AI management) are certified. SOC 2 is a roadmap / readiness item and is not presented as an achieved certification." },
    { q: "Where is data processed and stored?", a: "This depends on your chosen deployment — hosted, private cloud or customer-controlled — confirmed during technical discovery." },
    { q: "Can we review your security documentation?", a: "Yes — security and data-processing documentation is available to qualified prospects under NDA. See the Trust Centre." },
  ] },
];

export default function FaqPage() {
  useEffect(() => { document.title = "FAQs | norse3"; }, []);
  return (
    <div>
      <PageHero
        eyebrow="FAQs"
        title="Questions, answered."
        subtitle="Everything compliance, security, technology and operations teams tend to ask about norse3 — the platform, the products and how we handle trust and assurance."
      >
        <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90">
          <Link to="/book-a-demo">Book a walkthrough <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
        </Button>
      </PageHero>

      {GROUPS.map((g, gi) => (
        <section key={g.title} className={`py-12 lg:py-14 ${gi % 2 === 1 ? "bg-navy-950/40 border-y border-white/10" : ""}`}>
          <div className="mx-auto max-w-3xl px-5 lg:px-8">
            <Reveal><SectionHeading eyebrow={`0${gi + 1}`} title={g.title} /></Reveal>
            <div className="mt-6 space-y-3">
              {g.items.map((it) => (
                <details key={it.q} className="group rounded-xl border border-white/10 bg-navy-900/60 p-5" data-testid="faq-item">
                  <summary className="cursor-pointer list-none text-sm font-semibold text-foreground flex items-center justify-between">{it.q}<ArrowDown className="h-4 w-4 text-primary transition-transform group-open:rotate-180" /></summary>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{it.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      ))}

      <section className="py-16 text-center">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground">Still have a question?</h2>
          <Button asChild className="mt-6 bg-primary text-primary-foreground hover:bg-primary/90"><Link to="/book-a-demo">Talk to our team <ArrowRight className="ml-1.5 h-4 w-4" /></Link></Button>
        </div>
      </section>
    </div>
  );
}
